import { Platform } from "react-native";

/** Web: Titel sind fokussierbar, ohne in der Tab-Reihenfolge zu liegen. */
export const webTitleProps = Platform.OS === "web" ? { tabIndex: -1 as const } : {};

type Tabbable = {
  tabIndex: number;
  focus: () => void;
  hasAttribute: (name: string) => boolean;
  getAttribute: (name: string) => string | null;
};

function tabbableElements(root: { querySelectorAll: (selector: string) => ArrayLike<Tabbable> }) {
  return Array.from(
    root.querySelectorAll("a[href], button, input, select, textarea, [tabindex]"),
  ).filter((el) => {
    if (el.tabIndex < 0) return false;
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-disabled") === "true") return false;
    return true;
  });
}

/**
 * Hält Tab und Fokus im Overlay. Der Hintergrund bleibt separat inert,
 * solange die Sperre aktiv ist – unabhängig von Gate oder Notfall-Screen.
 */
export function bindWebOverlayTrap(
  root: ({ querySelectorAll: (selector: string) => ArrayLike<Tabbable> } & {
    contains: (node: Node) => boolean;
  }) | null,
  reclaim: () => void,
): () => void {
  if (Platform.OS !== "web") return () => {};
  const doc = (globalThis as { document?: Document }).document;
  if (!doc?.addEventListener || !root?.querySelectorAll) return () => {};

  const onKey = (event: { key?: string; shiftKey?: boolean; preventDefault?: () => void }) => {
    if (event.key !== "Tab") return;
    const items = tabbableElements(root);
    const current = doc.activeElement;
    const index = current ? items.indexOf(current as unknown as Tabbable) : -1;
    event.preventDefault?.();
    if (items.length === 0) {
      reclaim();
      return;
    }
    const next = event.shiftKey
      ? items[(index <= 0 ? items.length : index) - 1]
      : items[index < 0 || index === items.length - 1 ? 0 : index + 1];
    next?.focus();
  };

  const onFocusIn = (event: { target?: EventTarget | null }) => {
    const target = event.target;
    if (target && root.contains(target as Node)) return;
    reclaim();
  };

  doc.addEventListener("keydown", onKey as EventListener);
  doc.addEventListener("focusin", onFocusIn as EventListener);
  return () => {
    doc.removeEventListener("keydown", onKey as EventListener);
    doc.removeEventListener("focusin", onFocusIn as EventListener);
  };
}
